/**
 * Core domain types (mirror the D1 schema, §5). Identifiers in English.
 * Money fields are Bs unless suffixed `_usd`. Dates are 'YYYY-MM-DD' (Caracas).
 */

export const SEDE_CODES = ['CCS', 'BQTO', 'VAL', 'SC', 'PAR', 'MGTA', 'AD'] as const;
export type SedeCodigo = (typeof SEDE_CODES)[number];

export type Origen = 'gemini' | 'manual' | 'sql';
export type TasaFuente = 'scrape' | 'api' | 'manual';
export type Currency = 'BS' | 'USD';

export interface Sede {
  id: number;
  codigo: SedeCodigo;
  nombre: string;
  nombreReporte?: string | null;
  tarifaRackUsd?: number | null;
  activo: boolean;
}

/** A point in the BCV rate series. `fecha` is the date the rate is observed. */
export interface TasaPoint {
  fecha: string;
  tasa: number;
  fuente: TasaFuente;
}

/**
 * Daily record — one row per sede + fecha. Captures the FULL flash (§2.2).
 * All counts are nullable because a flash may be partial / pre-commit.
 */
export interface RegistroDiario {
  sedeId: number;
  fecha: string;

  // capacity / availability
  totalRooms: number | null;
  ooo: number | null;
  oos: number | null;

  // occupancy
  ocupadasSinCh: number | null; // Rooms Occupied minus Comp and House Use
  house: number | null;
  comp: number | null;
  diamond: number | null; // filled separately; NOT in the flash (§2.2)

  // rate
  adrBs: number | null; // ADR minus Comp and House (Bs)
  tasaBcv: number | null;
  tarifaUsd: number | null;
  pctOcupacion: number | null; // sin Comp/House, 0..1

  // analytics (Bs)
  totalRevenueBs: number | null;
  roomRevenueBs: number | null;
  fnbRevenueBs: number | null;
  individualRevBs: number | null;
  individualRooms: number | null;
  companyRevBs: number | null;
  companyRooms: number | null;
  taRevBs: number | null; // travel agent
  taRooms: number | null;
  blockRevBs: number | null;
  blockRooms: number | null;
  inHousePersons: number | null;
  inHouseAdults: number | null;
  inHouseChildren: number | null;
  avgPersonRateBs: number | null;
  revparBs: number | null;

  // demand / pickup
  walkin: number | null;
  dayUse: number | null;
  noShow: number | null;
  earlyDeparture: number | null;
  extendedStay: number | null;
  reservationsMade: number | null;
  cancellations: number | null;

  // pace / on-the-books
  paceNext7: number | null;
  paceNext31: number | null;
  paceNext365: number | null;

  origen: Origen;
}

/** MONTH column from the closing flash — for reconciliation (§2.3.7). */
export interface CierreMesFlash {
  sedeId: number;
  anio: number;
  mes: number;
  roomsOccupiedMonth: number | null;
  ocupadasSinChMonth: number | null;
  houseMonth: number | null;
  compMonth: number | null;
  pctOcupacionMonth: number | null;
  roomRevenueMonthBs: number | null;
  totalRevenueMonthBs: number | null;
}

/** Validation issue raised by the rules engine (§2.3). */
export type IssueSeverity = 'error' | 'warning' | 'info';
export interface ValidationIssue {
  code: string;
  severity: IssueSeverity;
  message: string; // Spanish (UI surface)
  field?: string;
}

/** Derived daily figures (deterministic — never from Gemini). */
export interface DailyDerived {
  ocupadasTotales: number;
  disponibles: number | null;
  pctOcupacion: number | null; // sin Comp/House over physical total rooms
  pctOcupacionConHouse: number | null;
  tarifaUsd: number | null;
  adrUsd: number | null;
  revparUsd: number | null;
  trevparUsd: number | null;
  roomRevenueUsd: number | null;
  totalRevenueUsd: number | null;
  diamondShare: number | null; // diamond / ocupadasSinCh
  compShare: number | null;
  houseShare: number | null;
}

/** A KPI value paired with its comparison delta (§9, §10). */
export interface KpiValue {
  value: number | null;
  comparison?: number | null;
  deltaAbs?: number | null;
  deltaPct?: number | null; // fraction (0.41 = +41%)
}
